import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Casca das quatro superfícies de Auth — canvas da T1.1, §3.1 e §3.4.
 *
 * A casca existe como componente, e não como classe copiada em cada página, porque o ritmo do
 * canvas (largura de 420 px, respiro de `--sp-4`/`--sp-8`, banda de cabeçalho de 44 px e vinheta
 * dourada) é o que faz as quatro telas parecerem a mesma aplicação. Repetir o bloco em quatro
 * lugares garante que a quinta tela divirja.
 *
 * 🔴 Não usar a classe `.app` do `components.css`: lá ela é a grelha com sidebar do guia visual.
 * A casca do app compõe com utilitários sobre os mesmos tokens.
 *
 * §4.4 do canvas: o scroll vertical continua a existir — por toque, roda e teclado —, só o widget
 * da barra fica oculto. Não é `overflow: hidden`.
 */
export interface AuthScreenProps {
  /** Identidade estável da tela para E2E e depuração (`data-screen-label`). */
  screenLabel: string;
  /** Título da banda de cabeçalho. Ausente no Welcome, que abre com a marca. */
  title?: string;
  subtitle?: ReactNode;
  /** Quando presente, desenha o botão voltar de 44×44 px à esquerda do título. */
  onBack?: () => void;
  children: ReactNode;
}

export function AuthScreen({ screenLabel, title, subtitle, onBack, children }: AuthScreenProps) {
  const { t } = useTranslation();

  return (
    <main
      data-screen-label={screenLabel}
      className="min-h-[100dvh] w-full bg-bg bg-[image:var(--vignette)]"
    >
      <div className="mx-auto flex min-h-[100dvh] w-full min-w-0 max-w-[420px] flex-col px-4 py-8 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {title ? (
          <header className="flex min-h-[44px] min-w-0 items-center gap-2">
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                aria-label={t('actions.back')}
                className="-ml-[10px] flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-full text-ink-2 transition-colors duration-1 ease-soberano hover:text-gold focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus-visible:ring-2"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
            ) : null}
            <h1 className="min-w-0 font-display text-[length:var(--t-xl)] font-semibold leading-tight tracking-[-0.2px] text-ink-1 [text-wrap:pretty]">
              {title}
            </h1>
          </header>
        ) : null}

        {subtitle ? <p className="mt-2 text-ink-2 [text-wrap:pretty]">{subtitle}</p> : null}

        {children}
      </div>
    </main>
  );
}

/** Espaçador que empurra o rodapé da tela para baixo, como o `.grow` do canvas. */
export function AuthScreenSpacer() {
  return <div className="grow" aria-hidden="true" />;
}
